/**
 * Skeleton Loading Components
 * 
 * Provides skeleton placeholders for various UI elements to improve
 * perceived performance during data loading.
 */

import React from 'react';
import { CCard, CCardBody, CCardHeader, CRow, CCol } from '@coreui/react';

// Skeleton animation styles
const skeletonStyle = {
  backgroundColor: '#e9ecef',
  borderRadius: '4px',
  animation: 'skeleton-pulse 1.5s ease-in-out infinite',
};

// Add keyframes for skeleton animation
const SkeletonStyles = () => (
  <style>{`
    @keyframes skeleton-pulse {
      0% { opacity: 1; }
      50% { opacity: 0.4; }
      100% { opacity: 1; }
    }
  `}</style>
);

/**
 * Basic skeleton rectangle
 */
export const SkeletonBox = ({ width = '100%', height = '20px', style = {} }) => (
  <div
    style={{
      ...skeletonStyle,
      width,
      height,
      ...style,
    }}
  />
);

/**
 * Skeleton for a single line of text
 */
export const SkeletonText = ({ width = '100%', lines = 1, gap = '8px' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap }}>
    {[...Array(lines)].map((_, i) => (
      <SkeletonBox
        key={i}
        width={i === lines - 1 && lines > 1 ? '60%' : width}
        height="16px"
      />
    ))}
  </div>
);

/**
 * Skeleton for a circular avatar
 */
export const SkeletonAvatar = ({ size = 48 }) => (
  <div
    style={{
      ...skeletonStyle,
      width: size,
      height: size,
      borderRadius: '50%',
    }}
  />
);

/**
 * Skeleton for a card component
 */
export const SkeletonCard = ({ hasHeader = true, lines = 3 }) => (
  <CCard className="mb-4">
    {hasHeader && (
      <CCardHeader>
        <SkeletonBox width="40%" height="24px" />
      </CCardHeader>
    )}
    <CCardBody>
      <SkeletonText lines={lines} />
    </CCardBody>
  </CCard>
);

/**
 * Skeleton for a table row
 */
export const SkeletonTableRow = ({ columns = 5 }) => (
  <tr>
    {[...Array(columns)].map((_, i) => (
      <td key={i} style={{ padding: '12px' }}>
        <SkeletonBox width={i === 0 ? '80%' : '60%'} height="20px" />
      </td>
    ))}
  </tr>
);

/**
 * Skeleton for a full table
 */
export const SkeletonTable = ({ rows = 5, columns = 5 }) => (
  <>
    <SkeletonStyles />
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <SkeletonBox width="150px" height="24px" />
        <SkeletonBox width="100px" height="32px" />
      </CCardHeader>
      <CCardBody>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr>
              {[...Array(columns)].map((_, i) => (
                <th key={i} style={{ padding: '12px' }}>
                  <SkeletonBox width="70%" height="18px" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(rows)].map((_, i) => (
              <SkeletonTableRow key={i} columns={columns} />
            ))}
          </tbody>
        </table>
      </CCardBody>
    </CCard>
  </>
);

/**
 * Skeleton for a chart
 */
export const SkeletonChart = ({ height = '350px' }) => (
  <>
    <SkeletonStyles />
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <SkeletonBox width="180px" height="24px" />
        <div className="d-flex gap-2">
          <SkeletonBox width="100px" height="32px" />
          <SkeletonBox width="80px" height="32px" />
        </div>
      </CCardHeader>
      <CCardBody>
        {/* Type selection badges */}
        <div className="d-flex gap-2 mb-3">
          {[...Array(4)].map((_, i) => (
            <SkeletonBox key={i} width="80px" height="26px" style={{ borderRadius: '13px' }} />
          ))}
        </div>
        {/* Chart area */}
        <SkeletonBox width="100%" height={height} />
        {/* Summary stats */}
        <CRow className="mt-3">
          {[...Array(4)].map((_, i) => (
            <CCol key={i} xs={6} md={3}>
              <SkeletonBox width="100%" height="60px" />
            </CCol>
          ))}
        </CRow>
      </CCardBody>
    </CCard>
  </>
);

/**
 * Skeleton for the observations dashboard
 */
export const SkeletonObservationsDashboard = () => (
  <>
    <SkeletonStyles />
    <SkeletonChart />
    <SkeletonTable rows={5} columns={6} />
  </>
);

/**
 * Skeleton for a stats card row
 */
export const SkeletonStatsRow = ({ count = 4 }) => (
  <>
    <SkeletonStyles />
    <CRow className="mb-4">
      {[...Array(count)].map((_, i) => (
        <CCol key={i} sm={6} lg={3}>
          <CCard className="mb-4">
            <CCardBody className="d-flex align-items-center">
              <SkeletonAvatar size={48} />
              <div className="ms-3" style={{ flex: 1 }}>
                <SkeletonBox width="40%" height="14px" style={{ marginBottom: '8px' }} />
                <SkeletonBox width="60%" height="24px" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      ))}
    </CRow>
  </>
);

/**
 * Skeleton for a list item
 */
export const SkeletonListItem = ({ hasAvatar = true }) => (
  <div className="d-flex align-items-center p-3 border-bottom">
    {hasAvatar && <SkeletonAvatar size={40} />}
    <div className="ms-3" style={{ flex: 1 }}>
      <SkeletonBox width="60%" height="16px" style={{ marginBottom: '6px' }} />
      <SkeletonBox width="40%" height="14px" />
    </div>
    <SkeletonBox width="60px" height="24px" />
  </div>
);

/**
 * Skeleton for a list
 */
export const SkeletonList = ({ items = 5, hasAvatar = true }) => (
  <>
    <SkeletonStyles />
    <CCard className="mb-4">
      <CCardHeader>
        <SkeletonBox width="120px" height="24px" />
      </CCardHeader>
      <CCardBody className="p-0">
        {[...Array(items)].map((_, i) => (
          <SkeletonListItem key={i} hasAvatar={hasAvatar} />
        ))}
      </CCardBody>
    </CCard>
  </>
);

/**
 * Skeleton for dashboard widgets
 */
export const SkeletonDashboard = () => (
  <>
    <SkeletonStyles />
    <SkeletonStatsRow count={4} />
    <CRow>
      <CCol md={8}>
        <SkeletonChart height="300px" />
      </CCol>
      <CCol md={4}>
        <SkeletonList items={4} />
      </CCol>
    </CRow>
  </>
);

/**
 * Generic page loading skeleton
 */
export const SkeletonPage = ({ type = 'default' }) => {
  switch (type) {
    case 'table':
      return <SkeletonTable />;
    case 'chart':
      return <SkeletonChart />;
    case 'dashboard':
      return <SkeletonDashboard />;
    case 'observations':
      return <SkeletonObservationsDashboard />;
    case 'list':
      return <SkeletonList />;
    default:
      return (
        <>
          <SkeletonStyles />
          <SkeletonCard lines={5} />
        </>
      );
  }
};

export default SkeletonPage;
